<template>
  <FormSect class="FormArticleLaptops">
    <div class="title">
      <h3>{{ $t('title') }}</h3>
      <SelectArchArticle
        :article="article"
        :value="article.archArticleId"
        @update:value="setArchArticle" />
    </div>
    <TabsHead
      :labelActive.sync="sortLabelActive"
      :labels="sortsLabels">
      <Btn
        class="ib"
        :class="{ inv: !hasSettingsOpen }"
        @click="hasSettingsOpen = !hasSettingsOpen">
        <Icon name="cog" />
      </Btn>
    </TabsHead>
    <div class="laptopsButtons">
      <LaptopPickButtons
        :countToPick.sync="countToPick"
        :minimum="laptopBuckets.length"
        @clear="clearLaptops"
        @pick="pickLaptops" />
      <Btn
        class="ib"
        :isLoading="isFetchingLaptops"
        @click="fillProsConsForSelectedLaptops">
        {{ $t('fillProsCons') }}
      </Btn>
    </div>
    <FormLaptopsSettings
      v-if="hasSettingsOpen"
      :bucketsPrices="bucketsPrices"
      :isBucketView="isBucketView"
      :minimalScores.sync="criteria.minimalScores"
      :weights.sync="criteria.weights"
      @refresh="fetchLaptops"
      @update:bucketsPrices="setBucketsPrices" />
    <LaptopFilters
      :filtersLaptop.sync="filtersLaptop"
      @fetch="fetchLaptops" />
    <div v-if="laptopEvaluationsSorted.length || !isFetchingLaptops">
      <div
        v-for="(laptopBucket, index) in articleLaptopBuckets"
        :key="index"
        class="laptopBucket">
        <h3 v-if="laptopBucket.label">
          {{ laptopBucket.label }}
        </h3>
        <div v-if="laptopBucket.laptops.length">
          <div
            v-for="({ articleLaptop, laptop }) in laptopBucket.laptops"
            :key="laptop.id">
            <ModalSimilarLaptops
              v-if="laptopForReplacementSelectedId === laptop.id"
              :laptopToBeReplaced="laptop"
              :laptops="laptopBucketsNotSelectedLaptops[index]"
              @close="laptopForReplacementSelectedId = null"
              @replace="replaceLaptop(index, laptop.id, $event)" />
            <RecordArticleLaptop
              :alternativeTo="getAlternativeTo(laptop.id)"
              :alternatives="getLaptopAlternatives(laptop.id)"
              :articleLaptop="articleLaptop"
              :isSelected="laptopIdsSelected.includes(laptop.id)"
              :laptop="laptop"
              :topic="article.topic"
              @addAlternative="addAlternative(laptop.id, $event)"
              @generateProsConsForLaptop="generateProsConsForLaptop"
              @openLaptopsSimilarModal="openLaptopsSimilarModal(laptop.id)"
              @patched="fetchLaptops"
              @promote="promoteAlternative(laptop.id)"
              @removeAlternative="removeAlternative(laptop.id, $event)"
              @syncArticleLaptopPrice="syncArticleLaptopPrice(laptop.id)"
              @update:articleLaptop="setArticleLaptop(laptop.id, $event)"
              @update:isSelected="setLaptopIdIsSelected(laptop.id, $event)" />
          </div>
          <BucketNavigation
            :bucket="laptopBucket"
            :bucketResizeStep="bucketResizeStep"
            :isLoading="isFetchingLaptops"
            @offset="offsetBucketLimit($event, index)" />
        </div>
        <Empty v-else>
          {{ $t('noLaptops') }}
        </Empty>
      </div>
    </div>
    <Loader v-else class="lg" />
    <SheetsRow
      :isCreatingSpreadsheet="isCreatingSpreadsheet"
      :spreadsheetUrl="article.spreadsheetUrl"
      @create="createSheet"
      @update:spreadsheetUrl="upArticle(['spreadsheetUrl'], $event)" />
  </FormSect>
</template>

<script>
import { mergeDeepRight, propEq } from 'rambda'
import Alternatives from './Alternatives'
import Arch from './Arch'
import Buckets from './Buckets'
import Fetching from './Fetching'
import Filters from './Filters'
import FormLaptopsSettings from '@forms/FormLaptopsSettings'
import LaptopFilters from './Filters/LaptopFilters'
import Picking from './Picking'
import PriceSyncing from './PriceSyncing'
import ProsCons from './ProsCons'
import RecordArticleLaptop from '@components/board/RecordArticleLaptop'
import Replacement from './Replacement'
import Selecting from './Selecting'
import Sheets from './Sheets'
import Sorts from './Sorts'
import TabsHead from '@components/TabsHead'
import { getters } from '@utils'

export default {
  mixins: [
    Alternatives,
    Arch,
    Buckets,
    Fetching,
    Filters,
    Picking,
    PriceSyncing,
    ProsCons,
    Replacement,
    Selecting,
    Sheets,
    Sorts,
  ],
  components: {
    FormLaptopsSettings,
    LaptopFilters,
    RecordArticleLaptop,
    TabsHead,
  },
  props: {
    article: Object,

    // separate props for performance reasons
    articleLaptops: Array,
    criteria: Object,
  },
  data() {
    return {
      hasSettingsOpen: false,
    }
  },
  computed: getters('articleLaptopFactory'),
  methods: {
    upArticle(path, article) {
      return this.$up(['article', ...path], article)
    },
    setArticleLaptop(laptopId, data) {
      const articleLaptopIndex = this.articleLaptops
        .findIndex(propEq('laptopId', laptopId))

      const articleLaptop = this.articleLaptops[articleLaptopIndex]

      this.$up(
        ['articleLaptops', articleLaptopIndex],
        mergeDeepRight(articleLaptop, data),
      )
    },
    setArticleLaptops(articleLaptops) {
      this.$up('articleLaptops', articleLaptops.map(this.articleLaptopFactory))
    },
  },
}
</script>

<style lang="scss">
.FormArticleLaptops {
  > .title {
    display: flex;
    justify-content: space-between;

    > .SelectArticle {
      width: 18rem;
    }
  }

  .laptopBucket {
    > h3 {
      margin-bottom: $u;
    }
  }

  .laptopsButtons {
    align-items: center;
    display: flex;
    justify-content: flex-end;
    margin-bottom: $uh;

    .Btn {
      margin-left: $uh;
    }
  }
}
</style>

<i18n>
{
  "en": {
    "sorts": [
      "Best in price bracket",
      "Best",
      "Best Value"
    ],

    "fillProsCons": "Fill Pros and Cons",
    "noLaptops": "No laptops found",
    "title": "Laptops",
    "under": "Under {0}"
  }
}
</i18n>
